import React, { useState, useEffect, useRef } from 'react';
import { X, ShieldCheck, Loader2, CheckCircle2, Phone, Mail, ArrowLeft, CalendarCheck, LocateFixed } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AddressAutocomplete from '../maps/AddressAutocomplete';
import { getUserLocationWithCity } from '../../utils/locationUtils';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:7000/api';
const NAVY = '#07142A';
const YELLOW = '#FFD400';

const SERVICE_LABELS = {
  daily: 'Daily Rental',
  rental: 'Multi-Day Rental',
  'km-based': 'Pay Per KM',
  outstation: 'Outstation',
  local: 'Local Trips',
};

// Self-contained public booking flow used on the per-vehicle share page.
// Mirrors the OTP + /bookings/public contract from BrowseCars so the customer
// can book straight from the shared link without leaving the page.
export default function BookVehicleModal({ open, onClose, car }) {
  const { user, loginWithOtp } = useAuth();
  const [mounted, setMounted] = useState(false);

  const serviceTypes = (Array.isArray(car?.serviceTypes) && car.serviceTypes.length ? car.serviceTypes : ['daily'])
    .filter((t) => SERVICE_LABELS[t]);

  // step: 'verify' | 'form' | 'success'
  const [step, setStep] = useState(user ? 'form' : 'verify');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // OTP state
  const [otpStep, setOtpStep] = useState('enter'); // 'enter' | 'code'
  const [otpMethod, setOtpMethod] = useState('email'); // 'email' | 'phone' (SMS often off)
  const [otpEmail, setOtpEmail] = useState('');
  const [otpPhone, setOtpPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [timer, setTimer] = useState(0);

  // Booking form state
  const [form, setForm] = useState({
    name: '', phone: '', rentalType: serviceTypes[0] || 'daily',
    startDate: '', endDate: '', hours: '', estimatedKm: '',
    pickupLocation: '', dropoffLocation: '', notes: '',
  });
  const [result, setResult] = useState(null);
  const [locating, setLocating] = useState(false);

  const timerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setError('');
      setStep(user ? 'form' : 'verify');
      setOtpStep('enter');
      setOtp('');
      if (user) {
        setForm((f) => ({
          ...f,
          name: user.name && !/^(User|Customer) /.test(user.name) ? user.name : '',
          phone: user.phone || '',
        }));
      }
    }
  }, [open, user]);

  useEffect(() => {
    if (timer <= 0) return;
    timerRef.current = setInterval(() => setTimer((t) => t - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [timer]);

  if (!open && !mounted) return null;

  const close = () => { setMounted(false); setTimeout(() => onClose?.(), 200); };

  const cleanPhone = (p) => (p || '').trim().replace(/\D/g, '').slice(-10);

  // ── OTP ──────────────────────────────────────────────────────────────
  const sendOtp = async (resend = false) => {
    setError('');
    if (otpMethod === 'phone' && cleanPhone(otpPhone).length < 10) return setError('Enter a valid 10-digit phone number');
    if (otpMethod === 'email' && !otpEmail.includes('@')) return setError('Enter a valid email address');
    setLoading(true);
    try {
      const endpoint = otpMethod === 'phone' ? (resend ? '/users/resend-otp' : '/users/send-otp') : '/users/send-email-otp';
      const body = otpMethod === 'phone' ? { phone: cleanPhone(otpPhone) } : { email: otpEmail.trim() };
      const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) { setOtpStep('code'); setTimer(30); setOtp(''); }
      else setError(data.message || 'Failed to send OTP');
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setError('');
    if (otp.length < 6) return setError('Enter the 6-digit OTP');
    setLoading(true);
    try {
      const body = otpMethod === 'phone'
        ? { phone: cleanPhone(otpPhone), otp }
        : { email: otpEmail.trim(), otp };
      const res = await fetch(`${API_BASE}/users/verify-otp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        loginWithOtp(data.token, data.user);
        const phone = data.user?.phone || cleanPhone(otpPhone);
        const nm = data.user?.name || '';
        setForm((f) => ({ ...f, phone, name: /^(User|Customer) /.test(nm) ? '' : nm }));
        setStep('form');
      } else setError(data.message || 'Invalid OTP. Please try again.');
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  // ── Use my current location (geolocation → reverse geocode) ──────────
  const fillCurrentLocation = async (field) => {
    setError('');
    setLocating(true);
    try {
      const loc = await getUserLocationWithCity();
      const addr = loc.displayName || [loc.city, loc.state].filter(Boolean).join(', ');
      if (!addr) { setError('Could not determine your address. Please search instead.'); return; }
      setForm((f) => ({ ...f, [field]: addr }));
    } catch (err) {
      setError(err?.message || 'Location access denied. Please allow location or search instead.');
    } finally {
      setLocating(false);
    }
  };

  // ── Booking submit ───────────────────────────────────────────────────
  const submitBooking = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) return setError('Please enter your name');
    if (cleanPhone(form.phone).length < 10) return setError('Please enter a valid phone number');
    if (!form.pickupLocation.trim()) return setError('Please enter pickup location');
    if (!form.dropoffLocation.trim()) return setError('Please enter drop-off location');
    if (['km-based', 'outstation'].includes(form.rentalType) && !form.estimatedKm) return setError('Please enter estimated kilometers');

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/bookings/public`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          carId: car._id,
          phone: cleanPhone(form.phone),
          name: form.name.trim(),
          startDate: form.startDate || new Date().toISOString(),
          endDate: form.endDate || form.startDate || new Date().toISOString(),
          pickupLocation: form.pickupLocation.trim(),
          dropoffLocation: form.dropoffLocation.trim(),
          estimatedKm: form.estimatedKm ? parseInt(form.estimatedKm) : 0,
          hours: form.hours ? parseInt(form.hours) : 0,
          notes: form.notes.trim(),
          rentalType: form.rentalType,
        }),
      });
      const data = await res.json();
      if (data.success) { setResult(data); setStep('success'); }
      else setError(data.message || 'Request failed. Please try again.');
    } catch { setError('Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const isLocal = form.rentalType === 'local';
  const needsKm = ['km-based', 'outstation'].includes(form.rentalType);

  const inputCls = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-gray-400 focus:ring-2 focus:ring-gray-100 outline-none text-sm';
  const labelCls = 'block text-xs font-semibold text-gray-500 mb-1';

  return (
    <div onClick={close} className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
      style={{ background: 'rgba(7,20,42,0.55)', backdropFilter: 'blur(4px)', opacity: open && mounted ? 1 : 0, transition: 'opacity .2s ease' }}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
        style={{ transform: open && mounted ? 'translateY(0) scale(1)' : 'translateY(24px) scale(.98)', transition: 'transform .28s cubic-bezier(.16,1,.3,1)' }}>

        {/* Header */}
        <div className="sticky top-0 bg-white px-6 pt-6 pb-4 flex items-center justify-between border-b border-gray-100 z-10">
          <div>
            <h3 className="text-lg font-bold" style={{ color: NAVY }}>
              {step === 'success' ? 'Request sent' : 'Book this vehicle'}
            </h3>
            {car && <p className="text-xs text-gray-400 mt-0.5">{[car.make, car.model, car.year].filter(Boolean).join(' ')}</p>}
          </div>
          <button onClick={close} className="p-2 rounded-full hover:bg-gray-100 text-gray-500"><X size={20} /></button>
        </div>

        <div className="px-6 py-5">
          {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-2.5">{error}</div>}

          {/* STEP 1 — Verify */}
          {step === 'verify' && (
            <div>
              <div className="flex items-center gap-2 mb-4 text-sm text-gray-500">
                <ShieldCheck size={16} style={{ color: '#16a34a' }} /> Quick verification keeps bookings genuine
              </div>

              {otpStep === 'enter' && (
                <>
                  <div className="flex p-1 mb-4 rounded-xl bg-gray-100">
                    {[['email', 'Email', Mail], ['phone', 'Phone', Phone]].map(([m, label, Icon]) => (
                      <button key={m} onClick={() => { setOtpMethod(m); setError(''); }}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-semibold transition-all"
                        style={otpMethod === m ? { background: NAVY, color: '#fff' } : { color: '#6b7280' }}>
                        <Icon size={14} /> {label}
                      </button>
                    ))}
                  </div>
                  {otpMethod === 'email' ? (
                    <>
                      <label className={labelCls}>Email address</label>
                      <input type="email" value={otpEmail} onChange={(e) => setOtpEmail(e.target.value)} placeholder="you@example.com" className={inputCls} />
                    </>
                  ) : (
                    <>
                      <label className={labelCls}>Phone number</label>
                      <input type="tel" value={otpPhone} onChange={(e) => setOtpPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="10-digit mobile" className={inputCls} />
                    </>
                  )}
                  <button onClick={() => sendOtp(false)} disabled={loading}
                    className="w-full mt-4 py-3 rounded-xl font-bold flex items-center justify-center gap-2" style={{ background: YELLOW, color: NAVY }}>
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null} Send OTP
                  </button>
                </>
              )}

              {otpStep === 'code' && (
                <>
                  <button onClick={() => { setOtpStep('enter'); setError(''); }} className="flex items-center gap-1 text-sm text-gray-500 mb-3"><ArrowLeft size={14} /> Change {otpMethod}</button>
                  <label className={labelCls}>Enter the 6-digit code sent to {otpMethod === 'email' ? otpEmail : otpPhone}</label>
                  <input inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="______" className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`} style={{ color: NAVY }} />
                  <button onClick={verifyOtp} disabled={loading}
                    className="w-full mt-4 py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2" style={{ background: NAVY }}>
                    {loading ? <Loader2 size={18} className="animate-spin" /> : null} Verify & continue
                  </button>
                  <div className="text-center mt-3 text-sm text-gray-400">
                    {timer > 0 ? `Resend in ${timer}s` : <button onClick={() => sendOtp(true)} className="font-semibold" style={{ color: NAVY }}>Resend OTP</button>}
                  </div>
                </>
              )}
            </div>
          )}

          {/* STEP 2 — Booking form */}
          {step === 'form' && (
            <form onSubmit={submitBooking} className="space-y-4">
              {serviceTypes.length > 1 && (
                <div>
                  <label className={labelCls}>Booking type</label>
                  <div className="flex flex-wrap gap-2">
                    {serviceTypes.map((t) => (
                      <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, rentalType: t }))}
                        className="px-3 py-1.5 rounded-full text-sm font-semibold border transition-all"
                        style={form.rentalType === t ? { background: NAVY, color: '#fff', borderColor: NAVY } : { color: '#6b7280', borderColor: '#e5e7eb' }}>
                        {SERVICE_LABELS[t]}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div><label className={labelCls}>Your name</label><input value={form.name} onChange={set('name')} className={inputCls} placeholder="Full name" /></div>
                <div><label className={labelCls}>Phone</label><input type="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value.replace(/\D/g, '').slice(0, 10) }))} className={inputCls} placeholder="10-digit" /></div>
              </div>

              {isLocal ? (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Date</label><input type="date" value={form.startDate} onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value, endDate: e.target.value }))} className={inputCls} /></div>
                  <div><label className={labelCls}>Hours</label><input type="number" min="1" value={form.hours} onChange={set('hours')} className={inputCls} placeholder="e.g. 4" /></div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <div><label className={labelCls}>Start date</label><input type="date" value={form.startDate} onChange={set('startDate')} className={inputCls} /></div>
                  <div><label className={labelCls}>End date</label><input type="date" value={form.endDate} onChange={set('endDate')} className={inputCls} /></div>
                </div>
              )}

              {needsKm && (
                <div><label className={labelCls}>Estimated km</label><input type="number" min="1" value={form.estimatedKm} onChange={set('estimatedKm')} className={inputCls} placeholder="Approx. distance" /></div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className={labelCls} style={{ marginBottom: 0 }}>Pickup location</label>
                  <button type="button" onClick={() => fillCurrentLocation('pickupLocation')} disabled={locating}
                    className="text-xs font-semibold flex items-center gap-1 disabled:opacity-50" style={{ color: NAVY }}>
                    {locating ? <Loader2 size={12} className="animate-spin" /> : <LocateFixed size={12} />} Use current location
                  </button>
                </div>
                <AddressAutocomplete label="" value={form.pickupLocation}
                  onChange={(loc) => setForm((f) => ({ ...f, pickupLocation: loc.address }))}
                  placeholder="Search pickup address..." />
              </div>
              <div>
                <label className={labelCls}>Drop-off location</label>
                <AddressAutocomplete label="" value={form.dropoffLocation}
                  onChange={(loc) => setForm((f) => ({ ...f, dropoffLocation: loc.address }))}
                  placeholder="Search drop-off address..." />
              </div>
              <div><label className={labelCls}>Notes (optional)</label><textarea value={form.notes} onChange={set('notes')} rows={2} className={inputCls} placeholder="Anything the owner should know" /></div>

              <button type="submit" disabled={loading}
                className="w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2" style={{ background: YELLOW, color: NAVY }}>
                {loading ? <Loader2 size={18} className="animate-spin" /> : <CalendarCheck size={18} />} Send booking request
              </button>
              <p className="text-center text-xs text-gray-400">The owner reviews & confirms. No payment now.</p>
            </form>
          )}

          {/* STEP 3 — Success */}
          {step === 'success' && (
            <div className="text-center py-4">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: '#dcfce7' }}>
                <CheckCircle2 size={34} style={{ color: '#16a34a' }} />
              </div>
              <h4 className="text-xl font-bold" style={{ color: NAVY }}>Booking request sent!</h4>
              <p className="text-gray-500 text-sm mt-2">{car?.make} {car?.model} — the owner will contact you shortly to confirm.</p>
              {(result?.customer?.phone || form.phone) && (
                <div className="mt-4 inline-block px-4 py-2 rounded-xl bg-gray-50 border border-gray-200 text-sm">
                  Track with phone <span className="font-bold" style={{ color: NAVY }}>{result?.customer?.phone || cleanPhone(form.phone)}</span>
                </div>
              )}
              <div className="mt-6 flex gap-3">
                <a href="/track-booking" className="flex-1 py-3 rounded-xl font-semibold text-center text-white" style={{ background: NAVY }}>Track booking</a>
                <button onClick={close} className="flex-1 py-3 rounded-xl font-semibold border" style={{ borderColor: '#e5e7eb', color: NAVY }}>Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
